import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Empty,
  Popover,
  Space,
  Spin,
  Typography,
} from 'antd';
import { getMemberCommunicationTags } from '@/services/technical';
import {
  COMMUNICATION_SOURCE_LABELS,
  timeText,
} from './SimpleWorkspaceSupport';
import { readTagPage, tagState } from './MemberTagSupport';
import MemberTagEvidence from './MemberTagEvidence';
import MemberTagReviewPanel from './MemberTagReviewPanel';
import styles from './SimpleWorkspace.less';

const { Text, Paragraph } = Typography;

// 父组件以 memberId 为 key：切换会员时销毁旧状态，迟到响应也不能写入新会员页面。
export default function MemberTagPanel({ memberId }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const version = ++generation.current;
    setLoading(true);
    setError('');
    setPage(null);
    getMemberCommunicationTags({
      requestData: { memberId, beforeCursor: cursor, limit: 20 },
    })
      .then((response) => {
        if (generation.current === version)
          setPage(readTagPage(response, memberId, cursor));
      })
      .catch((failure) => {
        if (generation.current !== version) return;
        const status = failure?.response?.status;
        setError(
          status === 403
            ? '无会员档案菜单权限'
            : status === 401
            ? '请先登录后台'
            : '标签暂时无法加载，请重试',
        );
      })
      .finally(() => {
        if (generation.current === version) setLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [memberId, cursor, retryVersion]);

  return (
    <Card
      size="small"
      bordered={false}
      title="会员标签"
      className={`${styles.infoCard} ${styles.memberTagCard}`}
    >
      <Paragraph type="secondary" className={styles.memberTagIntro}>
        问卷标签由明确答案生成，沟通标签从训练证据中提取。点击标签可查看依据与复核记录；标签不代表医学诊断。
      </Paragraph>
      <Spin spinning={loading}>
        {error ? (
          <Alert
            type="warning"
            showIcon
            message={error}
            action={
              <Button
                size="small"
                onClick={() => setRetryVersion((value) => value + 1)}
              >
                重试
              </Button>
            }
          />
        ) : page?.items.length ? (
          <Space size={[8, 12]} wrap>
            {page.items.map((item) => {
              const state = tagState(item.displayState);
              return (
                <Popover
                  key={item.observationId}
                  trigger="click"
                  title={item.name}
                  content={
                    <div style={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                      <Paragraph>{item.summary}</Paragraph>
                      <Paragraph type="secondary">
                        来源：
                        {COMMUNICATION_SOURCE_LABELS[item.sourceType] ||
                          '其他沟通来源'}
                        <br />
                        记录时间：{timeText(item.occurredAt)}
                        <br />
                        状态：{state.label}
                        {item.validUntil ? (
                          <>
                            <br />
                            有效期至：{timeText(item.validUntil)}
                          </>
                        ) : null}
                      </Paragraph>
                      <Text type="secondary">
                        证据编号：{item.sourceEvidenceId}
                      </Text>
                      <MemberTagReviewPanel
                        key={`review:${memberId}:${item.observationId}`}
                        memberId={memberId}
                        observationId={item.observationId}
                      />
                      {[
                        'questionnaire',
                        'guided_course',
                        'live_coaching',
                        'workwx_chat',
                      ].includes(item.sourceType) && (
                        <MemberTagEvidence
                          key={`${memberId}:${item.observationId}`}
                          memberId={memberId}
                          observationId={item.observationId}
                          expectedKind={
                            item.sourceType === 'questionnaire'
                              ? 'QUESTIONNAIRE_ANSWER'
                              : item.sourceType === 'workwx_chat'
                              ? 'CHAT_MESSAGE'
                              : 'COURSE_SUMMARY'
                          }
                        />
                      )}
                    </div>
                  }
                >
                  <Button size="small" className={styles.memberTagButton}>
                    {item.name}
                  </Button>
                </Popover>
              );
            })}
          </Space>
        ) : !loading ? (
          <Empty
            className={styles.memberTagEmpty}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              page?.hasMore
                ? '本页没有可展示的动态标签，可继续查看更早记录'
                : '本页暂无动态标签'
            }
          />
        ) : (
          <div style={{ minHeight: 64 }} />
        )}
      </Spin>
      {page && (
        <div className={styles.memberTagFooter}>
          <Space wrap>
            {page.invalidCount > 0 && (
              <Text type="warning">{page.invalidCount} 条数据异常未展示</Text>
            )}
            {page.hasMore && (
              <Button
                size="small"
                disabled={loading}
                onClick={() => setCursor(page.nextCursor)}
              >
                更早标签
              </Button>
            )}
            {cursor && (
              <Button
                size="small"
                disabled={loading}
                onClick={() => setCursor(null)}
              >
                回到最新
              </Button>
            )}
          </Space>
        </div>
      )}
    </Card>
  );
}

MemberTagPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
